/**
 * OpenDocument style resolution.
 *
 * ODF splits formatting the way HWPX does — a paragraph carries a *name*, not
 * properties — but unlike HWPX it splits the names across two files and lets
 * them inherit. `content.xml` holds the automatic styles a writer generates per
 * paragraph (`P1`, `T3`); `styles.xml` holds the named styles a person chose
 * ("Heading 1", "Standard") and the defaults everything falls back to. A style
 * points at its parent with `style:parent-style-name`, and the chain can be
 * several links long.
 *
 * So this module reads both parts into one table and flattens the chain once,
 * at parse time. Two consequences matter:
 *
 * - **Nothing here is ever written back.** The tables are read for display
 *   only. An edit rewrites a paragraph's text and leaves its `text:style-name`
 *   exactly as it was, so every other paragraph pointing at that style still
 *   means what it meant (§13, §24).
 * - **A missing `styles.xml` is not an error.** The text is still readable; it
 *   just renders unstyled, with a warning saying so rather than a refusal
 *   (§34).
 *
 * Heading level is resolved here too, because ODF states it in three different
 * places — `text:outline-level` on the element, the style name
 * `Heading_20_1`, and `style:default-outline-level` on the style — and a reader
 * that checks only the first shows a heading-shaped document as flat text.
 */
import { attr, childElements, findElements, lengthToPx } from './scan.js';

export type Alignment = 'left' | 'center' | 'right' | 'justify';

export interface OdfTextStyle {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly sizePx: number | null;
  readonly color: string | null;
  readonly highlight: string | null;
  readonly vertical: 'super' | 'sub' | null;
  readonly fontName: string | null;
}

export interface OdfParagraphStyle {
  readonly alignment: Alignment | null;
  readonly indentPx: number;
}

export interface OdfStyle {
  readonly name: string;
  /** `paragraph`, `text`, `graphic`, `table-cell`, `presentation`, … */
  readonly family: string;
  readonly displayName: string;
  readonly parent: string | null;
  readonly text: OdfTextStyle;
  readonly paragraph: OdfParagraphStyle;
  /** Set when the style itself declares an outline level. */
  readonly outlineLevel: number | null;
}

export interface OdfStyleTable {
  readonly styles: ReadonlyMap<string, OdfStyle>;
  /** Family → the `style:default-style` for it. */
  readonly defaults: ReadonlyMap<string, OdfStyle>;
  /** List style name → true when its first level is numbered rather than bulleted. */
  readonly orderedLists: ReadonlyMap<string, boolean>;
  /** True when neither part declared a single style. */
  readonly empty: boolean;
}

const NEUTRAL_TEXT: OdfTextStyle = {
  bold: false, italic: false, underline: false, strike: false,
  sizePx: null, color: null, highlight: null, vertical: null, fontName: null,
};

const NEUTRAL_PARAGRAPH: OdfParagraphStyle = { alignment: null, indentPx: 0 };

/* ------------------------------------------------------------------ *
 * reading
 * ------------------------------------------------------------------ */

/**
 * Build one table from the style parts.
 *
 * Both `content.xml` and `styles.xml` carry an `office:automatic-styles`
 * element, and the names in them are in the same space — a document's `P1` is
 * `P1` wherever it was declared. Later declarations win, which is why
 * `content.xml` is read last: a writer that redefines a name in the content
 * part meant the content part's version.
 */
export function parseStyles(stylesXml: string, contentXml: string): OdfStyleTable {
  const styles = new Map<string, OdfStyle>();
  const defaults = new Map<string, OdfStyle>();
  const orderedLists = new Map<string, boolean>();

  for (const xml of [stylesXml, contentXml]) {
    if (!xml) continue;
    for (const span of findElements(xml, 'style:style')) {
      const style = readStyle(xml, span);
      if (style) styles.set(style.name, style);
    }
    for (const span of findElements(xml, 'style:default-style')) {
      const family = attr(span.attrs, 'style:family');
      if (!family) continue;
      const style = readStyle(xml, span, `__default:${family}`);
      if (style) defaults.set(family, style);
    }
    for (const span of findElements(xml, 'text:list-style')) {
      const name = attr(span.attrs, 'style:name');
      if (!name) continue;
      const levels = childElements(xml, span, [
        'text:list-level-style-number',
        'text:list-level-style-bullet',
        'text:list-level-style-image',
      ]);
      const first = levels.find((l) => attr(l.attrs, 'text:level') === '1') ?? levels[0];
      orderedLists.set(name, first?.name === 'text:list-level-style-number');
    }
  }

  return { styles, defaults, orderedLists, empty: styles.size === 0 && defaults.size === 0 };
}

function readStyle(
  xml: string, span: { attrs: string; innerStart: number; innerEnd: number },
  forcedName?: string,
): OdfStyle | null {
  const name = forcedName ?? attr(span.attrs, 'style:name');
  const family = attr(span.attrs, 'style:family') ?? '';
  if (!name) return null;

  const props = (elementName: string): string => {
    const child = childElements(xml, span as never, [elementName])[0];
    return child ? child.attrs : '';
  };

  const textProps = props('style:text-properties');
  const paraProps = props('style:paragraph-properties');
  const outline = attr(span.attrs, 'style:default-outline-level');

  return {
    name,
    family,
    displayName: attr(span.attrs, 'style:display-name') ?? decodeStyleName(name),
    parent: attr(span.attrs, 'style:parent-style-name'),
    text: readTextProperties(textProps),
    paragraph: readParagraphProperties(paraProps),
    outlineLevel: outline ? clampLevel(Number(outline)) : null,
  };
}

function readTextProperties(attrs: string): OdfTextStyle {
  if (!attrs) return NEUTRAL_TEXT;
  const weight = attr(attrs, 'fo:font-weight');
  const style = attr(attrs, 'fo:font-style');
  const underline = attr(attrs, 'style:text-underline-style');
  const strike = attr(attrs, 'style:text-line-through-style');
  const position = attr(attrs, 'style:text-position');
  const color = attr(attrs, 'fo:color');
  const highlight = attr(attrs, 'fo:background-color');

  return {
    // `fo:font-weight` is a CSS weight, not a flag: `bold`, `bolder` and any
    // numeric weight from 600 up all mean bold, and `normal` explicitly
    // *unsets* an inherited bold.
    bold: weight !== null && weight !== 'normal'
      && (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600),
    italic: style === 'italic' || style === 'oblique',
    underline: underline !== null && underline !== 'none',
    strike: strike !== null && strike !== 'none',
    sizePx: lengthToPx(attr(attrs, 'fo:font-size')),
    color: color && color !== 'transparent' ? color.replace(/^#/, '') : null,
    highlight: highlight && highlight !== 'transparent' ? highlight.replace(/^#/, '') : null,
    // The value is a percentage of the line, positive up: `super 58%`.
    vertical: position === null ? null
      : /^super\b/.test(position) || /^\s*\+?[1-9]/.test(position) ? 'super'
        : /^sub\b/.test(position) || /^\s*-/.test(position) ? 'sub' : null,
    fontName: attr(attrs, 'style:font-name') ?? attr(attrs, 'fo:font-family'),
  };
}

function readParagraphProperties(attrs: string): OdfParagraphStyle {
  if (!attrs) return NEUTRAL_PARAGRAPH;
  const align = attr(attrs, 'fo:text-align');
  return {
    // ODF uses the XSL-FO writing-mode-relative names, which resolve to left
    // and right in every script this release lays out.
    alignment: align === 'center' ? 'center'
      : align === 'end' || align === 'right' ? 'right'
        : align === 'justify' ? 'justify'
          : align === 'start' || align === 'left' ? 'left' : null,
    indentPx: Math.max(0, lengthToPx(attr(attrs, 'fo:margin-left')) ?? 0),
  };
}

/* ------------------------------------------------------------------ *
 * resolution
 * ------------------------------------------------------------------ */

/** Guard against a cyclic `style:parent-style-name` chain in a damaged file. */
const MAX_DEPTH = 12;

function chain(table: OdfStyleTable, name: string | null, family: string): OdfStyle[] {
  const out: OdfStyle[] = [];
  let current = name;
  for (let i = 0; i < MAX_DEPTH && current; i++) {
    const style = table.styles.get(current);
    if (!style) break;
    out.push(style);
    current = style.parent;
  }
  const fallback = table.defaults.get(family);
  if (fallback) out.push(fallback);
  // Nearest first is the order the merge below expects.
  return out;
}

/**
 * Flatten a style name into the text formatting it means.
 *
 * `extra` is the automatic text style a `text:span` carries, which sits closest
 * to the character and therefore wins over the paragraph's own style.
 */
export function resolveText(
  table: OdfStyleTable, styleNames: readonly (string | null)[],
): OdfTextStyle {
  // Later names are nearer the character, so they are applied last.
  const links: OdfStyle[] = [];
  for (const name of styleNames) {
    if (!name) continue;
    links.unshift(...chain(table, name, 'text'), ...chain(table, name, 'paragraph'));
  }

  let out = NEUTRAL_TEXT;
  // Furthest ancestor first: each nearer declaration overwrites what it states.
  for (const style of links.reverse()) out = mergeText(out, style.text);
  return out;
}

function mergeText(base: OdfTextStyle, over: OdfTextStyle): OdfTextStyle {
  return {
    // A boolean here is "declared on", and `false` can equally mean "not
    // declared". Merging with `||` would make an explicit `fo:font-weight`
    // of `normal` unable to switch an inherited bold back off — but the parser
    // has already collapsed that distinction, so the false case is inherited
    // rather than overriding. That is the conservative direction: a heading
    // that inherits bold from its family renders bold.
    bold: over.bold || base.bold,
    italic: over.italic || base.italic,
    underline: over.underline || base.underline,
    strike: over.strike || base.strike,
    sizePx: over.sizePx ?? base.sizePx,
    color: over.color ?? base.color,
    highlight: over.highlight ?? base.highlight,
    vertical: over.vertical ?? base.vertical,
    fontName: over.fontName ?? base.fontName,
  };
}

/** Flatten a paragraph style name into alignment and indent. */
export function resolveParagraph(
  table: OdfStyleTable, styleName: string | null,
): OdfParagraphStyle {
  let out = NEUTRAL_PARAGRAPH;
  for (const style of chain(table, styleName, 'paragraph').reverse()) {
    out = {
      alignment: style.paragraph.alignment ?? out.alignment,
      indentPx: style.paragraph.indentPx || out.indentPx,
    };
  }
  return out;
}

/**
 * Heading level for a paragraph, from every place ODF can state one.
 *
 * `declared` is `text:outline-level` on a `text:h`, which is authoritative when
 * present. Otherwise the style chain is followed: a paragraph styled
 * "Heading 2" is a heading even when it was written as a `text:p`, and writers
 * do emit that.
 */
export function headingLevel(
  table: OdfStyleTable, styleName: string | null, declared: string | null,
): number | null {
  if (declared) {
    const level = clampLevel(Number(declared));
    if (level !== null) return level;
  }
  for (const style of chain(table, styleName, 'paragraph')) {
    if (style.outlineLevel !== null) return style.outlineLevel;
    const fromName = levelFromName(style.displayName) ?? levelFromName(style.name);
    if (fromName !== null) return fromName;
  }
  return levelFromName(styleName ?? '');
}

/**
 * ODF encodes characters that are illegal in an XML name as `_20_` — a
 * underscore-delimited hexadecimal code point. "Heading 1" is stored as
 * `Heading_20_1`, so the display name has to be decoded before it is read.
 */
export function decodeStyleName(name: string): string {
  return name.replace(/_([0-9a-fA-F]{2,6})_/g, (whole, hex: string) => {
    const code = Number.parseInt(hex, 16);
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff
      ? String.fromCodePoint(code)
      : whole;
  });
}

function levelFromName(name: string): number | null {
  const decoded = decodeStyleName(name);
  const m = /^(?:Heading|Title|제목|개요)\s*([1-9])?$/i.exec(decoded.trim());
  if (!m) return null;
  if (/^(?:Title|제목)$/i.test(decoded.trim())) return 1;
  return m[1] ? clampLevel(Number(m[1])) : null;
}

function clampLevel(value: number): number | null {
  if (!Number.isFinite(value) || value < 1) return null;
  return Math.min(6, Math.trunc(value));
}
